import { useState } from "react";
import { 
  checkDeltaGreaterThanZero,
  checkNumberMissing,
  checkValidNumbers,
  convertRealChainToNumber,
  validateRealChain
} from "../../helperFunctions/validateRealChain";
import { isValidCoordinate } from "@utils/locationUtils";


export const useAGMValidation = () => {
  const [validationErrors, setValidationErrors] = useState([]);
  const [realChainValidationErrors, setRealChainValidationErrors] = useState([]);
  const [realChainDeltaValidationErrors, setRealChainDeltaValidationErrors] = useState([]);
  const [latLngMissing, setLatLngMissing] = useState(false);

  /**
   * Validate an array of AGM rows.
   *
   * The function resets internal state and then performs several passes:
   *   1. convert real chain fields to numbers
   *   2. simple row‑level checks (missing/invalid values, delta <= 0)
   *   3. lat/lng presence
   *   4. pairwise comparisons for real chain distance warnings
   *
   * Hard failures (steps 2‑3) accumulate `validationErrors` with {row,column}
   * and return an object with `result:false`.  The caller may display an error
   * and the table will highlight the cells.
   *
   * Warnings (step 4) use the existing `realChainErrors`/`realChainDeltaErrors`
   * mechanism; the hook also populates `validationErrors` so the table can
   * highlight the relevant cells.  The returned object includes these arrays
   * and `result:false` so the caller can prompt the user but still submit.
   *
   * A plain Error instance is returned in the edge case of conversion failure or
   * when latitude/longitude validation fails; the caller treats it the same as
   * before.
   *
   * @param {Array} agmData - rows being validated
   * @returns {Object|Error} either Error or {result:boolean, [validationErrors],
   *   [realChainErrors], [realChainDeltaErrors], [latLngMissing], [message]}
   */
  const validateAgms = (agmData) => {
    // reset all validation states
    setValidationErrors([]);
    setRealChainValidationErrors([]);
    setRealChainDeltaValidationErrors([]);
    setLatLngMissing(false);
    
    const agmDataCopy = agmData.map(agm => ({ ...agm }));
    const convertedAgmData = convertRealChainToNumber(agmDataCopy);

    if (!convertedAgmData) {
      // conversion failure is unexpected; fall back to existing behaviour
      return new Error('Real Chain Values Must Be Numbers');
    }

    // collect row/column errors from the simple checks
    let validationErrors = [];
    let message = ''

    const deltaErrors = checkDeltaGreaterThanZero(convertedAgmData);
    if (deltaErrors.length){
      validationErrors.push(...deltaErrors);
      message = 'Real Chain Delta must be greater than 0 - adjust real chain value (if two real chain values must be the same, increase the second value by 0.01)'
    } 

    const numErrors = checkValidNumbers(convertedAgmData);
    if (numErrors.length){
      validationErrors.push(...numErrors);
      message = 'Real Chain values must be numbers and greater than 0 (only Launch can be 0)'
    } 

    const missingErrors = checkNumberMissing(convertedAgmData);
    if (missingErrors.length){
      validationErrors.push(...missingErrors);
      message = 'Make sure all Real Chain values are present and correct'
    } 

    if (validationErrors.length) {
      setValidationErrors(validationErrors);
      return {
        result: false,
        validationErrors,
        message,
      };
    }

    const { result, errors } = validateAllLatLng(convertedAgmData);

    if (!result) {
      setValidationErrors(errors);
      return new Error('Estimated lat/lng OR Real lat/lng must be filled in');
    }

    validationErrors = [];
    let realChainErrors = [];
    let realChainDeltaErrors = [];

    //check each pair of agms
    for (let i = 0; i < convertedAgmData.length - 1; i++) {
      const realChainInValid = validateRealChain(convertedAgmData[i], convertedAgmData[i + 1]);
      const deltaIsLarge = Number(convertedAgmData[i].Real_Chain_Delta) > 50;

      //if not valid, highlight both boxes in the table, and add to list of real chain errors
      if (realChainInValid) {
        validationErrors.push({ row: i, column: "Real_Chain" });
        validationErrors.push({ row: i + 1, column: "Real_Chain" });

        realChainErrors.push(realChainInValid);
      }

      //if real chain delta is large, user may have entered meters instead of kilometers - check if they still want to submit
      if(deltaIsLarge){
        validationErrors.push({ row: i, column: "Real_Chain_Delta" });
        realChainDeltaErrors.push(convertedAgmData[i])
      }

    }
    if(validationErrors.length || realChainErrors.length || realChainDeltaErrors.length){
      setValidationErrors(validationErrors);
      setRealChainValidationErrors(realChainErrors);
      setRealChainDeltaValidationErrors(realChainDeltaErrors);

      return {
        result: false,
        validationErrors,
        realChainErrors,
        realChainDeltaErrors,
        latLngMissing
      }
    }
    return {
      result: true
    }
  }

  return { 
    validateAgms,
    validationErrors,
    setValidationErrors,
    realChainValidationErrors,
    setRealChainValidationErrors,
    realChainDeltaValidationErrors
  };
};

/**
 * Validate all rows for Lat/Lng
 * @param {Array} rows - array of AGM rows
 * @returns {Object} - { result: true } or { result: false, validationErrors: [...] }
 */
const validateAllLatLng = (rows) => {
  const validationErrors = [];

  rows.forEach((row, rowIndex) => {
    // Latitude check
    const hasLat = isValidCoordinate(row.Est_Lat) || isValidCoordinate(row.Real_Lat);
    if (!hasLat) {
      validationErrors.push({ row: rowIndex, column: 'Est_Lat' });
      validationErrors.push({ row: rowIndex, column: 'Real_Lat' });
    }

    // Longitude check
    const hasLng = isValidCoordinate(row.Est_Lng) || isValidCoordinate(row.Real_Lng);
    if (!hasLng) {
      validationErrors.push({ row: rowIndex, column: 'Est_Lng' });
      validationErrors.push({ row: rowIndex, column: 'Real_Lng' });
    }
  });

  if (validationErrors.length === 0) {
    return { result: true };
  }

  return { result: false, errors: validationErrors };
};
